/* ============================================================
   SINC — spinning mark: black face, white edge, glow.

   Geometry: under an orthographic projection a shape rotated by t
   about Y is the silhouette scaled horizontally by cos(t), with each
   depth slice at x = z*sin(t). Slices are drawn back-to-front and
   their count is recomputed per frame from the projected depth, so
   the side wall never bands and the spin stays smooth.
   ============================================================ */
(function () {
  'use strict';

  function init(root) {
    var canvas = root.querySelector('canvas');
    if (!canvas || canvas.dataset.ready) return;
    canvas.dataset.ready = '1';

    var ctx = canvas.getContext('2d');
    var period  = parseFloat(root.dataset.speed || '16') * 1000;
    var depthPct= parseFloat(root.dataset.depth || '9') / 100;
    var tiltDeg = parseFloat(root.dataset.tilt || '-6');
    var glowAmt = parseFloat(root.dataset.glow || '18');
    var borderPx= parseFloat(root.dataset.border || '3');
    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var canBlur = typeof ctx.filter === 'string';

    var sprite = new Image();
    sprite.crossOrigin = 'anonymous';

    var scr = document.createElement('canvas'), sctx = scr.getContext('2d');
    var glow = document.createElement('canvas'), gctx = glow.getContext('2d');

    var W = 0, H = 0, dpr = 1;

    function size() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      var r = canvas.getBoundingClientRect();
      W = Math.max(1, Math.round(r.width));
      H = Math.max(1, Math.round(r.height));
      canvas.width = scr.width = glow.width = Math.round(W * dpr);
      canvas.height = scr.height = glow.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      gctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    /* silhouette flooded with one colour: source-in keeps the sprite's
       alpha and swaps the paint, so it follows the letterforms */
    function stamp(paint, w, h) {
      sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sctx.globalCompositeOperation = 'source-over';
      sctx.globalAlpha = 1;
      sctx.clearRect(0, 0, W, H);
      sctx.drawImage(sprite, (W - w) / 2, (H - h) / 2, w, h);
      sctx.globalCompositeOperation = 'source-in';
      sctx.fillStyle = paint;
      sctx.fillRect(0, 0, W, H);
      sctx.globalCompositeOperation = 'source-over';
      return scr;
    }

    /* white outline: the silhouette stamped around a small circle, so
       the border has even weight on every side at any rotation */
    function ring(target, src, cx, r, alpha) {
      target.globalAlpha = alpha;
      for (var a = 0; a < 16; a++) {
        var ang = a / 16 * Math.PI * 2;
        target.drawImage(src, cx + Math.cos(ang) * r, Math.sin(ang) * r, W, H);
      }
      target.globalAlpha = 1;
    }

    function draw(now) {
      var t = reduced ? -0.42 : (now % period) / period * Math.PI * 2;
      var cos = Math.cos(t), sin = Math.sin(t);
      var tilt = tiltDeg * Math.PI / 180;

      var ar = sprite.naturalWidth / sprite.naturalHeight;
      var h = Math.min(H * 0.72, (W * 0.80) / ar) * Math.cos(tilt) * 0.98;
      var w = h * ar;

      var depth = w * depthPct;
      var projected = Math.abs(depth * sin);
      var slices = Math.max(2, Math.min(180, Math.ceil(projected * dpr) + 2));
      var faceW = Math.max(0.001, Math.abs(w * cos));
      var front = (depth / 2) * sin;
      var back  = -front;
      var bw = Math.max(1, borderPx);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.scale(1, Math.cos(tilt));
      ctx.translate(-W / 2, -H / 2);

      // ---- glow: a blurred white silhouette sitting behind everything ----
      if (glowAmt > 0 && canBlur) {
        var white = stamp('#FFFFFF', faceW, h);
        gctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        gctx.globalCompositeOperation = 'source-over';
        gctx.clearRect(0, 0, W, H);
        gctx.filter = 'blur(' + glowAmt + 'px)';
        gctx.globalAlpha = 0.85;
        gctx.drawImage(white, front, 0, W, H);
        gctx.filter = 'blur(' + (glowAmt * 2.4) + 'px)';
        gctx.globalAlpha = 0.55;
        gctx.drawImage(white, front, 0, W, H);
        gctx.filter = 'none';
        gctx.globalAlpha = 1;
        ctx.drawImage(glow, 0, 0, W, H);
      }

      // ---- back face edge, dimmed so the depth reads ----
      var dim = stamp('rgba(255,255,255,0.30)', faceW, h);
      ring(ctx, dim, back, bw, 1);

      // ---- side wall: lifted just off pure black so it separates ----
      var wall = stamp('#121214', faceW, h);
      for (var i = 0; i < slices; i++) {
        var z = (i / (slices - 1) - 0.5) * depth;
        ctx.drawImage(wall, z * sin, 0, W, H);
      }

      // ---- front: white border, then black fill ----
      var line = stamp('#FFFFFF', faceW, h);
      ring(ctx, line, front, bw, 1);
      ctx.drawImage(stamp('#000000', faceW, h), front, 0, W, H);

      ctx.restore();
    }

    var raf = null;
    function loop(now) { draw(now); raf = requestAnimationFrame(loop); }

    sprite.onload = function () {
      size();
      if (reduced) { draw(0); return; }
      raf = requestAnimationFrame(loop);
    };
    sprite.src = root.dataset.sprite;

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { size(); if (reduced) draw(0); }, 150);
    }, { passive: true });

    if ('IntersectionObserver' in window && !reduced) {
      new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (e.isIntersecting && raf === null) raf = requestAnimationFrame(loop);
          else if (!e.isIntersecting && raf !== null) { cancelAnimationFrame(raf); raf = null; }
        });
      }, { threshold: 0 }).observe(canvas);
    }
    document.addEventListener('visibilitychange', function () {
      if (reduced) return;
      if (document.hidden && raf !== null) { cancelAnimationFrame(raf); raf = null; }
      else if (!document.hidden && raf === null) raf = requestAnimationFrame(loop);
    });
  }

  function boot() { document.querySelectorAll('[data-logo3d]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
